import { ref } from 'vue'
import axios from 'axios'
import { defineStore } from 'pinia'
import api from '@/services/api/settings'
import { getError } from '@/utils/errors'
import { reportError } from '@/services/error-reporting'
import { isLocalStorageAvailable } from '@/utils/local-storage-availability'

/**
 * Settings interface to document available settings
 */
interface AppSettings {
  terms_of_service_url?: string
  ferry_schedule_url?: string
  customer_support_phone?: string
  support_email_address?: string
  business_name?: string
  display_name?: string
  customer_support_email?: string
  // Controls whether tickets are created as individual or group by default
  // Values: 'individual' or 'group'
  tickets_bundling_method?: string
  [key: string]: string | undefined
}

export const useSettingStore = defineStore('setting', () => {
  const bucketUrl = import.meta.env.VITE_S3_BUCKET_URL
  const settingsS3Path = import.meta.env.VITE_S3_SETTINGS_PATH
  const settings = ref<AppSettings>({})
  const loaded = ref(false)
  const loading = ref(false)
  const SETTINGS_LOCALSTORAGE = 'app-settings'

  async function loadSettings() {
    if (loaded.value) return
    if (loading.value) return

    loading.value = true

    try {
      await loadSettingsFromLocalStorage()

      const s3Result = await loadSettingsFromS3()

      if (s3Result.success && s3Result.data) {
        settings.value = s3Result.data
        loaded.value = true
        updateLocalStorage()
      } else {
        const dbResult = await loadSettingsFromDB()

        if (dbResult.success && dbResult.data) {
          settings.value = dbResult.data
          loaded.value = true
          updateLocalStorage()
        }
      }
    } finally {
      loading.value = false
    }
  }

  async function loadSettingsFromS3() {
    try {
      const result = await axios.get(`${bucketUrl}${settingsS3Path}?t=${Date.now()}`)
      return { success: true, data: result.data }
    } catch (error: unknown) {
      return { success: false, error: error }
    }
  }

  async function loadSettingsFromDB() {
    const response = await api.getSetting('web')

    if (!response.error) {
      return { success: true, data: response }
    } else {
      return { success: false, error: getError(response) }
    }
  }

  async function loadSettingsFromLocalStorage() {
    try {
      const storedSettings = localStorage.getItem(SETTINGS_LOCALSTORAGE)

      if (storedSettings) {
        try {
          const parsedSettings = JSON.parse(storedSettings)

          if (
            parsedSettings &&
            typeof parsedSettings === 'object' &&
            !Array.isArray(parsedSettings)
          ) {
            settings.value = parsedSettings
            loaded.value = true
          }
        } catch (error: unknown) {
          // Do nothing
        }
      }
    } catch (error: unknown) {
      reportError(error, {
        name: 'Error when loading settings from local storage',
        context: { error },
        tags: 'info, settings'
      })
    }
  }

  async function updateLocalStorage() {
    if (!isLocalStorageAvailable()) return
    if (!settings.value || Object.keys(settings.value).length === 0) return

    try {
      localStorage.setItem(SETTINGS_LOCALSTORAGE, JSON.stringify(settings.value))
    } catch (error: unknown) {
      // Storage can be blocked or full after the availability probe passed
    }
  }

  return {
    loaded,
    settings,
    loadSettings
  }
})
